// ─── Help.tsx ─────────────────────────────────────────────────────────────────

import { useState } from 'react';

type Section = 'lookalike' | 'afterpush' | 'direct' | 'compliance' | 'clientbrief';

function SectionBtn({ id, active, onClick, icon, label }: {
  id: Section; active: boolean; onClick: () => void; icon: string; label: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
        background: active ? 'rgba(59,130,246,0.12)' : 'transparent',
        border: active ? '1px solid rgba(59,130,246,0.25)' : '1px solid #111927',
        color: active ? '#60a5fa' : '#4a5d7a',
        fontFamily: 'Sora, sans-serif', fontSize: 13, fontWeight: active ? 600 : 400,
        transition: 'all 0.15s',
      }}
    >
      <span style={{ fontSize: 16 }}>{icon}</span>
      {label}
    </button>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 16, fontWeight: 700, color: '#e8edf5',
      marginBottom: 6, marginTop: 28, letterSpacing: '-0.01em',
    }}>{children}</div>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, color: '#94a3b8',
      marginBottom: 4, marginTop: 20, letterSpacing: '0.05em', textTransform: 'uppercase',
    }}>{children}</div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.75, marginBottom: 8 }}>
      {children}
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 12, alignItems: 'flex-start' }}>
      <div style={{
        width: 26, height: 26, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        background: '#0d1a30', border: '1px solid #1e3a5f',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: '#3b82f6',
      }}>{n}</div>
      <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

function FlowBar({ steps }: { steps: { label: string; color?: string }[] }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      margin: '16px 0 24px', flexWrap: 'wrap',
    }}>
      {steps.map((s, i) => (
        <div key={s.label} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            padding: '6px 14px', borderRadius: 6,
            background: s.color ?? '#0d1a30',
            border: '1px solid #1e3a5f',
            fontSize: 12, fontWeight: 600, color: '#94a3b8',
            whiteSpace: 'nowrap',
          }}>{s.label}</div>
          {i < steps.length - 1 && (
            <div style={{ color: '#1e3a5f', fontSize: 18, padding: '0 4px' }}>→</div>
          )}
        </div>
      ))}
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'warn' | 'success'; children: React.ReactNode }) {
  const colors = {
    info:    { bg: 'rgba(37,99,235,0.07)',  border: 'rgba(59,130,246,0.2)',  icon: 'ℹ', color: '#60a5fa'  },
    warn:    { bg: 'rgba(234,179,8,0.07)',   border: 'rgba(234,179,8,0.2)',   icon: '⚠', color: '#fbbf24'  },
    success: { bg: 'rgba(16,185,129,0.07)', border: 'rgba(16,185,129,0.2)', icon: '✓', color: '#10b981' },
  }[type];
  return (
    <div style={{
      background: colors.bg, border: `1px solid ${colors.border}`,
      borderRadius: 8, padding: '10px 14px', marginBottom: 14,
      display: 'flex', gap: 10, alignItems: 'flex-start',
    }}>
      <span style={{ color: colors.color, fontSize: 14, flexShrink: 0, marginTop: 1 }}>{colors.icon}</span>
      <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

// ─── Client Brief ─────────────────────────────────────────────────────────────

const CLIENT_BRIEF_TEXT = [
  "ADVERTISING & DATA PRACTICES — WHAT WE DO AND DON'T DO",
  "",
  "This document explains how we use data to run your advertising campaigns.",
  "Written in plain English so you know exactly what's happening.",
  "",
  "══════════════════════════════════════════════════════",
  "✅  WHAT WE DO (and why it's allowed)",
  "══════════════════════════════════════════════════════",
  "",
  "1. We place a tracking pixel on your website.",
  "   What it does: A small piece of code fires when someone loads a page on your site.",
  "   It records that a visit happened — page URL, timestamp, rough location.",
  "   Why it's allowed: This is standard practice for every website that runs ads.",
  "   We only process visitor data when the visitor has accepted your cookie consent banner.",
  "   Source: https://www.facebook.com/business/help/952192354843755",
  "",
  "2. We match visitors to real identities using licensed data.",
  "   What it does: We cross-reference visitor signals with a licensed B2B contact",
  "   database to identify who visited your site — name, work email, company.",
  "   Why it's allowed: We use business contact data only (no health, financial, or",
  "   sensitive data). All personal data is hashed (scrambled into a one-way fingerprint)",
  "   before being sent to Meta — Meta never receives a readable email address.",
  "   Source: https://www.facebook.com/business/help/112061095610075",
  "",
  "3. We build a Custom Audience in your Meta Ads account.",
  "   What it does: The matched visitor list is uploaded to Meta. Meta compares it",
  "   against its own user database and creates a private audience inside your ad account.",
  "   Only you can use this audience — we can't and don't share it with anyone else.",
  "   Why it's allowed: Meta's Custom Audience product is used by millions of businesses.",
  "   You accept Meta's Custom Audience Terms of Service once in Ads Manager.",
  "   Source: https://www.facebook.com/customaudiences/app/tos",
  "",
  "4. We build a Lookalike Audience from your visitors.",
  "   What it does: Meta takes your Custom Audience and finds millions of other Meta users",
  "   who statistically look similar — same demographics, interests, online behaviour —",
  "   but have never visited your site.",
  "   Why it's allowed: Meta generates the lookalike entirely from their own internal data.",
  "   We don't supply personal data to create it. You're targeting a statistical profile,",
  "   not specific individuals.",
  "   Source: https://www.facebook.com/business/help/164749007013531",
  "",
  "5. All personal data is hashed before leaving our system.",
  "   Every field (email, phone, name, location) is converted to a SHA-256 hash before",
  "   transmission. The original data cannot be recovered from the hash.",
  "   Meta uses the hash to match against their own hashed user database.",
  "   Source: https://developers.facebook.com/docs/marketing-api/audiences/guides/custom-audiences/#hash",
  "",
  "══════════════════════════════════════════════════════",
  "❌  WHAT WE DO NOT DO",
  "══════════════════════════════════════════════════════",
  "",
  "✗  We do NOT collect sensitive personal data — no health, financial, religious,",
  "   political, or children's data. Ever.",
  "",
  "✗  We do NOT sell your customer data to any third party. Data is used exclusively",
  "   for your campaigns in your Meta ad account.",
  "",
  "✗  We do NOT email or call the matched contacts. The data is used only for paid",
  "   advertising audiences inside Meta.",
  "",
  "✗  We do NOT process data for visitors who declined cookie consent.",
  "   Consent is checked before any data is recorded or exported.",
  "",
  "✗  We do NOT keep historical archives. Exports are overwritten daily —",
  "   only the current snapshot is ever stored.",
  "",
  "✗  We do NOT target by race, religion, health, sexual orientation, or any",
  "   protected characteristic. Meta's policy prohibits this.",
  "   Source: https://www.facebook.com/policies/ads/",
  "",
  "══════════════════════════════════════════════════════",
  "OPT-OUT & DATA RIGHTS",
  "══════════════════════════════════════════════════════",
  "",
  "Anyone can opt out at any time:",
  "• On your website: declining cookies via your consent banner stops all data collection.",
  "• After the fact: send a deletion request to [YOUR EMAIL] and we will suppress and",
  "  delete the contact record within 5 business days, removing them from all future",
  "  audiences.",
  "",
  "EU/UK visitors: your privacy policy must disclose that visitor data may be used for",
  "advertising purposes and shared with Meta as a data processor. Standard IAB-compliant",
  "consent banners (OneTrust, Cookiebot, etc.) cover this when configured correctly.",
  "",
  "California residents (CCPA): targeted advertising may qualify as 'sharing' personal",
  "information under CCPA. Ensure your privacy policy includes a 'Do Not Sell or Share",
  "My Personal Information' link.",
  "Source: https://oag.ca.gov/privacy/ccpa",
  "",
  "══════════════════════════════════════════════════════",
  "SUMMARY",
  "══════════════════════════════════════════════════════",
  "",
  "What we do is the same thing every major brand running Meta ads does — we just do it",
  "more systematically. The key safeguards are: consent before collection, hashing before",
  "transmission, and no repurposing of data beyond your own advertising.",
  "",
  "Questions? Ask us before running campaigns.",
].join('\n');

function ClientBriefSection() {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(CLIENT_BRIEF_TEXT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <div>
      <H2>Client Brief — Plain-English Marketing Permissions</H2>
      <P>
        Copy and paste this into an email or document to share with clients before
        running campaigns. It covers what intentFlo does with their data, what is not
        allowed, opt-out rights, and links to the relevant Meta and legal sources.
      </P>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button
          onClick={handleCopy}
          style={{
            padding: '7px 16px', borderRadius: 7, cursor: 'pointer',
            background: copied ? 'rgba(16,185,129,0.15)' : 'rgba(59,130,246,0.12)',
            border: copied ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(59,130,246,0.25)',
            color: copied ? '#10b981' : '#60a5fa',
            fontFamily: 'Sora, sans-serif', fontSize: 12, fontWeight: 600,
            transition: 'all 0.2s',
          }}
        >
          {copied ? '✓ Copied!' : '📋 Copy to clipboard'}
        </button>
      </div>
      <pre style={{
        background: '#060a10', border: '1px solid #111927',
        borderRadius: 10, padding: '20px 24px',
        fontSize: 12, color: '#94a3b8', lineHeight: 1.85,
        fontFamily: 'DM Mono, monospace',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 560, overflowY: 'auto',
      }}>{CLIENT_BRIEF_TEXT}</pre>
    </div>
  );
}

// ─── Section content ──────────────────────────────────────────────────────────

function LookalikeSection() {
  return (
    <div>
      <H2>Pixel → Custom Audience → Lookalike</H2>
      <P>
        This flow captures anonymous site visitors via the intentFlo pixel, enriches their
        identity, and syncs a matched Custom Audience to Meta. You then use that audience as
        the seed for a Lookalike — letting Meta find millions of people who share the same
        profile as your best site visitors.
      </P>

      <FlowBar steps={[
        { label: 'Pixel fires on site' },
        { label: 'Visitor enriched' },
        { label: 'CSV exported to S3' },
        { label: 'Hightouch syncs to Meta', color: 'rgba(37,99,235,0.12)' },
        { label: 'Custom Audience grows' },
        { label: 'You build Lookalike (once)', color: 'rgba(16,185,129,0.1)' },
      ]} />

      <H3>Step-by-step setup (per client)</H3>

      <Step n={1}>
        <strong style={{ color: '#e8edf5' }}>Install the pixel</strong> — Go to the <strong>Pixel tab</strong>,
        copy the snippet, and paste it into the client's website <code style={{ color: '#60a5fa', fontFamily: 'DM Mono, monospace' }}>&lt;head&gt;</code>.
        The pixel fires on every page load and sends anonymous visitor events to intentFlo.
        Confirm "Pixel installed" on the Clients checklist (step 3).
      </Step>

      <Step n={2}>
        <strong style={{ color: '#e8edf5' }}>Accept Meta Custom Audience Terms</strong> — The client
        must accept Meta's Custom Audience Terms of Service in their Ads Manager before any
        audience can be created. A direct link is shown in the checklist (step 4).
        This is a one-time action per ad account.
      </Step>

      <Step n={3}>
        <strong style={{ color: '#e8edf5' }}>Create a Pixel audience in intentFlo</strong> — Go to
        the <strong>Audiences tab → New Audience → Source: Pixel → Type: Seed (Lookalike)</strong>.
        This registers the audience so the daily builder knows to include it in exports.
        Completes step 6 in the checklist automatically.
      </Step>

      <Step n={4}>
        <strong style={{ color: '#e8edf5' }}>Setup Hightouch sync</strong> — Click
        ⚡ <strong>Setup Hightouch</strong> in step 7 of the checklist.
        intentFlo automatically creates the Hightouch S3 model, configures all 7 field
        mappings (email, phone, first name, last name, city, state, country), and saves the
        sync ID. No manual Hightouch steps required.
      </Step>

      <Step n={5}>
        <strong style={{ color: '#e8edf5' }}>Daily automatic sync</strong> — Every day at 2 am UTC,
        the audience-builder queries all enriched, consented pixel contacts for the client,
        exports a CSV to S3, and immediately triggers the Hightouch sync.
        Hightouch pushes the updated list to the Meta Custom Audience automatically.
        Minimum 50 contacts required before an export runs.
      </Step>

      <Step n={6}>
        <strong style={{ color: '#e8edf5' }}>Create the Lookalike in Meta (one-time)</strong> — Once
        the Custom Audience has been synced at least once (visible in Hightouch → Syncs),
        go to <strong>Meta Ads Manager → Audiences → find "
        {"<ClientName>"} – Pixel Audience" → Create Lookalike</strong>.
        Set country and size (1% = most similar, 10% = broadest).
        The Lookalike automatically refreshes as the seed Custom Audience grows — you never
        need to recreate it.
      </Step>

      <Callout type="info">
        Meta typically takes <strong>24–72 hours</strong> after the first sync to process and
        make the Custom Audience available. The Lookalike may show "Populating" for up to
        6–24 hours after creation — this is normal.
      </Callout>

      <Callout type="info">
        Meta requires a <strong>minimum of 100 matched users</strong> before an audience can be
        used in campaigns, and at least <strong>1,000 matched users</strong> before match rate
        statistics are shown.
      </Callout>
    </div>
  );
}

function DirectSection() {
  return (
    <div>
      <H2>Prospeo → Direct Custom Audience</H2>
      <P>
        This flow uses Prospeo to find net-new contacts that match your client's Ideal Customer
        Profile (ICP) — people who have never visited the site. The enriched contacts are
        exported to S3 and synced to a separate Meta Custom Audience for direct targeting or
        as a cold suppression list.
      </P>

      <FlowBar steps={[
        { label: 'Define ICP filters' },
        { label: 'Prospeo enriches contacts' },
        { label: 'Suppress opt-outs' },
        { label: 'CSV exported to S3' },
        { label: 'Hightouch syncs to Meta', color: 'rgba(37,99,235,0.12)' },
        { label: 'Custom Audience ready', color: 'rgba(16,185,129,0.1)' },
      ]} />

      <H3>Step-by-step</H3>

      <Step n={1}>
        <strong style={{ color: '#e8edf5' }}>Go to Pipeline tab</strong> — Select the client and
        the target audience. This audience must be created first in the Audiences tab
        (Source: Prospeo, Type: Seed or Suppression).
      </Step>

      <Step n={2}>
        <strong style={{ color: '#e8edf5' }}>Configure ICP filters</strong> — Set job titles,
        industries, company size, seniority, and geography that match your client's ideal buyer.
        More specific filters = higher relevance but lower volume.
      </Step>

      <Step n={3}>
        <strong style={{ color: '#e8edf5' }}>Run the pipeline</strong> — Click Run. Prospeo
        searches for matching contacts, enriches them with email and contact data, and stores
        results in DynamoDB. The job runs asynchronously — status updates in real time.
        Suppressed and opted-out contacts are automatically excluded.
      </Step>

      <Step n={4}>
        <strong style={{ color: '#e8edf5' }}>Export to S3</strong> — On completion, the pipeline
        writes a CSV to{' '}
        <code style={{ color: '#60a5fa', fontFamily: 'DM Mono, monospace' }}>
          s3://audience-platform-exports/pixel-audiences/{'<clientId>'}/members.csv
        </code>.
        This same path is what Hightouch reads as the model source.
      </Step>

      <Step n={5}>
        <strong style={{ color: '#e8edf5' }}>Sync to Meta</strong> — Click the{' '}
        <strong>Sync</strong> button on the audience row in the Audiences tab.
        This triggers the Hightouch sync which pushes the contact list to Meta.
        Use as a targeted Custom Audience directly in campaigns, or as a suppression list
        (exclude from campaigns to avoid showing ads to existing contacts).
      </Step>

      <Callout type="info">
        Prospeo contacts are <strong>cold</strong> — they have not opted in to the client's
        communications. Use this audience for Meta ad targeting only, not for email outreach,
        unless the client has a separate legal basis for contact (e.g. B2B legitimate interest
        in applicable jurisdictions).
      </Callout>
    </div>
  );
}

function ComplianceSection() {
  return (
    <div>
      <H2>Compliance & Data Handling</H2>
      <P>
        intentFlo is designed with privacy compliance built into the pipeline.
        The following controls are active by default.
      </P>

      <H3>Consent — Pixel contacts</H3>
      <Step n={1}>
        <strong style={{ color: '#e8edf5' }}>Consent gate</strong> — The pixel only captures
        visitor data when <code style={{ color: '#60a5fa', fontFamily: 'DM Mono, monospace' }}>consentGiven = true</code>.
        Contacts without consent are stored in DynamoDB but are <em>excluded</em> from all
        S3 exports and Hightouch syncs. Clients must ensure their cookie consent banner
        (e.g. OneTrust, Cookiebot) correctly signals consent to the intentFlo pixel.
      </Step>
      <Step n={2}>
        <strong style={{ color: '#e8edf5' }}>Suppression</strong> — Any contact with
        <code style={{ color: '#60a5fa', fontFamily: 'DM Mono, monospace' }}> suppressed = true</code>{' '}
        is excluded from all exports. The Suppress Contact Lambda marks contacts suppressed
        when they opt out. Suppressed contacts are never re-added to an audience — even if
        they revisit the site.
      </Step>

      <H3>Meta Custom Audience Terms</H3>
      <Step n={3}>
        <strong style={{ color: '#e8edf5' }}>ToS acceptance required</strong> — Each client's
        Meta ad account must accept the{' '}
        <a href="https://www.facebook.com/customaudiences/app/tos" target="_blank" rel="noopener noreferrer"
          style={{ color: '#60a5fa' }}>Meta Custom Audience Terms of Service</a>{' '}
        before any audience can be created or synced. This is enforced by Meta's API —
        syncs will fail with a 400 error if ToS has not been accepted.
        Direct link: <code style={{ color: '#60a5fa', fontFamily: 'DM Mono, monospace' }}>
          https://business.facebook.com/ads/manage/customaudiences/tos/?act={'<ad_account_id>'}
        </code>
      </Step>

      <H3>Data hashing</H3>
      <Step n={4}>
        <strong style={{ color: '#e8edf5' }}>Hightouch auto-hashes PII</strong> — All personally
        identifiable fields (email, phone, first name, last name, city, state, country) are
        automatically SHA-256 hashed by Hightouch before transmission to Meta's API.
        Raw PII never leaves Hightouch in plaintext. This is on by default and cannot be
        disabled without explicit configuration.
      </Step>

      <H3>Data retention</H3>
      <Step n={5}>
        <strong style={{ color: '#e8edf5' }}>S3 exports</strong> — CSVs in the export bucket
        are overwritten on each run (not appended). Only the current snapshot is stored.
        No historical copies are retained by default.
      </Step>
      <Step n={6}>
        <strong style={{ color: '#e8edf5' }}>DynamoDB contacts</strong> — Contact records
        are retained indefinitely until manually deleted or suppressed. Suppressed contacts
        remain in the table (so they stay excluded) but are not exported or synced.
      </Step>

      <H3>GDPR / CCPA considerations</H3>
      <Callout type="warn">
        If a contact submits a <strong>right to erasure (GDPR Art. 17)</strong> or{' '}
        <strong>deletion request (CCPA)</strong>, you must:
        (1) suppress the contact in intentFlo via the Suppress Contact API,
        (2) manually remove them from the Meta Custom Audience in Ads Manager,
        and (3) delete their DynamoDB record if full erasure is required.
        Hightouch's <em>Remove from list</em> delete mode will remove them from the Meta
        audience on the next sync if the row is deleted from the S3 CSV.
      </Callout>

      <Callout type="warn">
        Prospeo-sourced contacts are enriched from <strong>public and licensed B2B data</strong>.
        In EU/EEA jurisdictions, use of B2B contact data for ad targeting must rely on
        a valid legal basis (typically legitimate interest under GDPR Art. 6(1)(f)).
        Consult your legal counsel before using Prospeo audiences for EU-based clients.
      </Callout>

      <Callout type="success">
        Pixel contacts who have provided consent and whose data is used solely for
        retargeting/lookalike matching on Meta are generally covered by the client's
        existing privacy policy and cookie consent, provided the policy discloses
        third-party ad platform data sharing.
      </Callout>
    </div>
  );
}

function AfterPushSection() {
  return (
    <div>
      <H2>After Pushing Contacts to Meta</H2>
      <P>
        You've imported a contacts list and clicked "Push to Meta". Here's how to verify
        it worked and what to do next.
      </P>

      <FlowBar steps={[
        { label: 'CSV imported' },
        { label: 'Push to Meta clicked' },
        { label: 'S3 updated' },
        { label: 'Hightouch sync triggered', color: 'rgba(37,99,235,0.12)' },
        { label: 'Meta audience populated', color: 'rgba(16,185,129,0.1)' },
        { label: 'Run ads or build Lookalike', color: 'rgba(16,185,129,0.15)' },
      ]} />

      <H3>Step 1 — Verify the sync ran in Hightouch</H3>
      <Step n={1}>
        Go to <strong style={{ color: '#e8edf5' }}>hightouch.com → Syncs</strong> and find
        the sync named <strong style={{ color: '#e8edf5' }}>"[ClientName] – Pixel Audience"</strong>.
        It should show status <strong style={{ color: '#10b981' }}>Success</strong> and a
        recent "Last run" timestamp. If it shows an error, check the Hightouch sync logs
        for the specific row-level failure.
      </Step>

      <H3>Step 2 — Verify the audience in Meta Ads Manager</H3>
      <Step n={2}>
        Go to <strong style={{ color: '#e8edf5' }}>Meta Ads Manager → Audiences</strong>{' '}
        (<a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer"
          style={{ color: '#60a5fa' }}>business.facebook.com</a> → your ad account → Audiences).
      </Step>
      <Step n={3}>
        Find <strong style={{ color: '#e8edf5' }}>"[ClientName] – Pixel Audience"</strong>.
        It will show:
        <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 2 }}>
          <li><strong style={{ color: '#e8edf5' }}>Size</strong> — shown as a range (e.g. "1,000–5,000"). Meta fuzzes the exact number for privacy. Expect 40–70% match rate — 323 contacts typically yields ~130–225 matched profiles.</li>
          <li><strong style={{ color: '#e8edf5' }}>Availability</strong> — "Ready" means it can be used in campaigns. "Populating" means Meta is still processing — check back in 15–60 minutes.</li>
          <li><strong style={{ color: '#e8edf5' }}>Minimum size</strong> — Meta requires at least <strong>100 matched users</strong> before the audience can be used in campaigns.</li>
        </ul>
      </Step>

      <Callout type="info">
        Meta takes <strong>15 minutes to 24 hours</strong> after a Hightouch sync to reflect
        updated audience size. If the audience still shows 0 or "Populating" after 24 hours,
        check that the Hightouch sync ran successfully and that the S3 CSV contains valid
        email addresses.
      </Callout>

      <H3>Step 3 — What you can do with the audience</H3>

      <Step n={4}>
        <strong style={{ color: '#e8edf5' }}>Run ads directly (retargeting)</strong> — Create
        a new campaign in Meta Ads Manager. In the Ad Set, under Audiences, choose{' '}
        <strong style={{ color: '#e8edf5' }}>"Use a saved audience"</strong> → select the
        Custom Audience. This targets your exact contact list — ideal for reactivation
        campaigns, special offers, or brand reminders to warm contacts.
      </Step>

      <Step n={5}>
        <strong style={{ color: '#e8edf5' }}>Build a Lookalike (most powerful — do this once)</strong> —
        In Meta Audiences, select the Custom Audience → click{' '}
        <strong style={{ color: '#e8edf5' }}>"Create Lookalike"</strong>.
        <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 2 }}>
          <li>Set the <strong style={{ color: '#e8edf5' }}>country</strong> to the client's target market.</li>
          <li>Set the <strong style={{ color: '#e8edf5' }}>size</strong> — 1% = tightest match (smallest, highest quality), 10% = broadest reach (largest, more diluted). Start with 1–2%.</li>
          <li>The Lookalike <strong style={{ color: '#e8edf5' }}>auto-refreshes</strong> as your Custom Audience grows — you only build it once, ever.</li>
          <li>Meta typically generates 1–2 million people at 1% in a major market.</li>
        </ul>
      </Step>

      <Step n={6}>
        <strong style={{ color: '#e8edf5' }}>Exclude from campaigns (suppression)</strong> —
        Add the Custom Audience as an <strong style={{ color: '#e8edf5' }}>exclusion</strong> in
        any ad set to avoid showing cold prospecting ads to people already on your list.
        Useful when running broad campaigns — exclude existing clients to avoid wasted spend.
      </Step>

      <Callout type="success">
        <strong>Recommended workflow:</strong> Push contacts → wait for Meta to populate →
        build a 1% Lookalike → run cold prospecting ads to the Lookalike →
        run retargeting ads to the Custom Audience directly.
        Two campaigns, two audiences, zero audience overlap.
      </Callout>

      <Callout type="info">
        The audience is <strong>automatically refreshed every night at 2 am UTC</strong> as new
        pixel visitors come in. You don't need to push manually again — the Lookalike will
        grow on its own as the seed Custom Audience expands.
      </Callout>
    </div>
  );
}

// ─── Main Help component ──────────────────────────────────────────────────────

export default function Help() {
  const [section, setSection] = useState<Section>('lookalike');

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#e8edf5', letterSpacing: '-0.02em', marginBottom: 4 }}>
          Help & Documentation
        </div>
        <div style={{ fontSize: 13, color: '#3b4f6a' }}>
          How intentFlo flows work, and how to stay compliant.
        </div>
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 8, margin: '24px 0 32px', flexWrap: 'wrap' }}>
        <SectionBtn id="lookalike" active={section === 'lookalike'} onClick={() => setSection('lookalike')}
          icon="◎" label="Pixel → Lookalike Audience" />
        <SectionBtn id="afterpush" active={section === 'afterpush'} onClick={() => setSection('afterpush')}
          icon="▶" label="After Pushing Contacts to Meta" />
        <SectionBtn id="direct" active={section === 'direct'} onClick={() => setSection('direct')}
          icon="⟳" label="Prospeo → Direct Audience" />
        <SectionBtn id="compliance" active={section === 'compliance'} onClick={() => setSection('compliance')}
          icon="⊞" label="Compliance & Data Handling" />
        <SectionBtn id="clientbrief" active={section === 'clientbrief'} onClick={() => setSection('clientbrief')}
          icon="✉" label="Client Brief (copy-paste)" />
      </div>

      {/* Content */}
      <div style={{
        background: '#0b1220', border: '1px solid #111927',
        borderRadius: 12, padding: '8px 28px 28px',
      }}>
        {section === 'lookalike'   && <LookalikeSection />}
        {section === 'afterpush'   && <AfterPushSection />}
        {section === 'direct'      && <DirectSection />}
        {section === 'compliance'  && <ComplianceSection />}
        {section === 'clientbrief' && <ClientBriefSection />}
      </div>
    </div>
  );
}
